import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Lock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { contentApi } from "../api";
import { useState, FormEvent, useEffect } from "react";
import CubeLogo from "../components/CubeLogo";

export default function ViewerLayout() {
  const { data } = useQuery({ queryKey: ["content"], queryFn: contentApi.get });
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");

  useEffect(() => {
    if (location.pathname !== "/search") setQ("");
  }, [location.pathname]);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  }

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const el = document.getElementById("global-search") as HTMLInputElement | null;
        el?.focus();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-cream-100">
      <header className="bg-cream-100/85 backdrop-blur-xl sticky top-0 z-30 border-b border-ink-200/60">
        <div className="max-w-6xl mx-auto px-8 h-16 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 group">
            <CubeLogo size={28} />
            <div className="flex items-baseline gap-2">
              <span className="text-[15px] font-semibold tracking-tight text-ink-900">
                {data?.meta.portal_name ?? "CORE"}
              </span>
              <span className="hidden md:inline text-[11px] uppercase tracking-[0.14em] text-ink-500">
                Celebal Operations &amp; Resource Engine
              </span>
            </div>
          </Link>

          <form onSubmit={onSearch} className="flex-1 max-w-md ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
            <input
              id="global-search"
              className="w-full h-10 pl-10 pr-14 rounded-lg border border-ink-200 bg-white text-[13.5px] placeholder:text-ink-400 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 transition-colors shadow-sm"
              placeholder="Search services…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 kbd hidden sm:inline-flex">⌘K</kbd>
          </form>

          <Link to="/admin" className="btn-ghost h-9 px-3 hidden sm:inline-flex">
            <Lock className="size-3.5" /> Admin
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-ink-200/60">
        <div className="max-w-6xl mx-auto px-8 py-6 text-[12.5px] text-ink-500 flex items-center justify-between">
          <span>© {new Date().getFullYear()} <span className="display italic">Celebal Tech</span></span>
          <span className="display italic text-ink-700 tracking-tight">CORE</span>
        </div>
      </footer>
    </div>
  );
}
